import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import { yupResolver } from "@hookform/resolvers/yup";
import { validationSchema } from "../../../../service/validation/assigndriver";
import { ApiService } from "../../../../service/api.service";
import { MASTER_URL, OPERTIONS_URL } from "../../../../common/common";

// Components
import Input from "../../../../components/FormInputs";
import CustomDropDown from "../../../../components/Dropdown/CustomDropDown";
import DatePicker from "../../../../components/Datepicker";
import SecureImage from "../../../../components/BucketImage/Image";
// import { driverImg } from "../../../../common/svg";

interface AssignDriverModalProps {
  isOpen: boolean;
  onClose: () => void;
  tripID: string;
  bookingID: string;
}

const AssignDriverModal: React.FC<AssignDriverModalProps> = ({
  isOpen,
  onClose,
  tripID,
  bookingID,
}) => {
  interface AssignDriverFormValues {
    driverId: string;
    driverName?: string;
    task: string;
    startDate: string;
    endDate: string;
    isImmediate?: boolean;
    notes?: string;
  }

  const {
    register,
    handleSubmit,
    formState: { errors },
    trigger,
    reset,
    getValues,
    setValue,
  } = useForm<AssignDriverFormValues>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      driverId: "",
      driverName: "",
      task: "",
      startDate: "",
      endDate: "",
      isImmediate: false,
      notes: "",
    },
  });

  const [drivers, setDrivers] = useState<any[]>([]);
  const [selectedDriver, setSelectedDriver] = useState<string | number>("");
  const [assignedDrivers, setAssignedDrivers] = useState<any[]>([]);
  const apiService = new ApiService();

  const fetchDrivers = async () => {
    try {
      const response: any = await apiService.getApi(`${MASTER_URL}/driver/get-route`);
      if (response.status === "200") {
        console.log("Drivers fetched successfully:", response);
        const driversData = response.body.data.map((driver: any) => ({
          label: driver.driverName,
          value: driver.driverId,
          metaId: driver.id,
        }));
        setDrivers(driversData);
      }
    } catch (error) {
      console.error("Error fetching drivers:", error);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchDrivers();
    }
  }, [isOpen]);

  const addDriverData = async () => {
    // Trigger validation
    const isValid = await trigger(["task", "startDate", "endDate"]);

    if (!isValid) {
      console.warn("Form validation failed");
      return;
    }

    const formData = getValues() as {
      driverId: string;
      driverName?: string;
      task?: string;
      startDate?: string;
      endDate?: string;
      isImmediate?: boolean;
      notes?: string;
    };

    if (!formData.driverId) {
      console.warn("No driver selected");
      return;
    }

    const dataToStore = {
      driverId: formData.driverId,
      driverName: formData.driverName || "",
      task: formData.task || "",
      startDate: formData.startDate || "",
      endDate: formData.endDate || "",
      isImmediate: formData.isImmediate || false,
      notes: formData.notes || "",
      bookingId: bookingID,
      tripId: tripID,
    };

    console.log("Adding Driver Assignment:", dataToStore);
    setAssignedDrivers((prev) => [...prev, dataToStore]);

    // Reset form fields for next entry
    reset({
      driverId: "",
      // driverName: "",
      task: "",
      startDate: "",
      endDate: "",
      isImmediate: false,
      notes: "",
    });
    setSelectedDriver("");
  };

  const getDriverLabel = (driverId: string) => {
    const found = drivers.find(d => d.value === driverId);
    return found ? found.label : driverId;
  };

  const removeDriver = (index: number) => {
    setAssignedDrivers(prev => prev.filter((_, i) => i !== index));
  };

  const onSubmit = async (data: any) => {
    try {
      const payload = {
        driverDetails: assignedDrivers
      };

      const response: any = await apiService.postApi(`${OPERTIONS_URL}/driver/store-route`, payload);

      if (response.status === "200") {
        console.log("Driver assigned successfully:", response);
        reset();
        setAssignedDrivers([]);
      } else {
        console.error("Failed to assign driver:", response);
      }

      onClose();
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  const handleCancel = () => {
    reset();
    setAssignedDrivers([]);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-30 z-50">
      <div className="bg-white rounded-lg shadow-lg w-[1000px] max-h-[90vh] overflow-auto flex flex-col">
        {/* Modal Header */}
        <div className="flex justify-between items-center p-4 border-b">
          <h2 className="text-[18px] font-[600] font-poppins text-[#444FBD]">
            Assign Driver
          </h2>
          <button onClick={onClose} className="text-gray-600 hover:text-black">
            ✕
          </button>
        </div>

        {/* Modal Body */}
        <div className="flex-grow p-6 flex">
          {/* Left Side - Form */}
          <div className="w-1/2 pr-6">
            {/* Dropdown */}
            <div className="ml-10">
              <CustomDropDown
                register={register}
                name="driverName"
                errors={errors}
                options={drivers || []}
                label="Driver Name"
                onChange={(value: any) => {
                  setSelectedDriver(value);
                  setValue("driverId", value);
                }}
              />
            </div>

            <div className="flex flex-col gap-1 mt-2 mb-3 ml-10">
              <Input
                label="Task"
                name="task"
                errors={errors}
                register={register}
                placeholder="Enter Task Description"
                onBlur={() => trigger("task")}
              />
            </div>

            {/* Notes field */}
            <div className="flex flex-col gap-1 mt-2 mb-3 ml-10">
              <Input
                label="Notes"
                name="notes"
                errors={errors}
                register={register}
                placeholder="Enter Additional Notes"
                onBlur={() => trigger("notes")}
              />
            </div>

            {/* Date Pickers */}
            <div className="mt-4 ml-10">
              <DatePicker
                label="Starting Date & Time"
                onDateChange={(date: any) => setValue("startDate", date)}
                register={register("startDate")}
                name="startDate"
                errors={errors}
                allowPastDates={true}
                includeTime={true}
                disabled={false}
              />
            </div>

            <div className="flex items-center mt-1 ml-10">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  {...register("isImmediate")}
                  className="w-[14px] h-[14px]"
                />
                <span className="text-[#282828] text-[14px] font-normal">
                  Immediate
                </span>
              </label>
            </div>

            <div className="mt-4 ml-10">
              <DatePicker
                label="End Date & Time"
                onDateChange={(date: any) => setValue("endDate", date)}
                register={register("endDate")}
                name="endDate"
                errors={errors}
                allowPastDates={true}
                includeTime={true}
                disabled={false}
              />
            </div>

            <div className="mt-6 ml-10">
              <button
                type="button"
                onClick={addDriverData}
                className="px-4 py-2 bg-[#444FBD] text-white rounded-md hover:bg-opacity-90"
              >
                Add Driver
              </button>
            </div>
          </div>

          {/* Right Side - Driver Display or Illustration */}
          <div className="w-1/2 ml-6 flex flex-col border-[#C1C1C1] border-l border-dashed pl-6">
            {assignedDrivers.length > 0 ? (
              <div className="w-full">
                <h3 className="text-[16px] font-[500] text-[#444FBD] mb-4">Assigned Drivers</h3>
                <div className="max-h-[400px] overflow-auto">
                  {assignedDrivers.map((driver, index) => (
                    <div key={index} className="bg-gray-50 rounded-md p-4 mb-3 relative">
                      <button
                        type="button"
                        onClick={() => removeDriver(index)}
                        className="absolute top-2 right-2 text-gray-500 hover:text-red-500"
                      >
                        ✕
                      </button>
                      <h4 className="font-medium">{getDriverLabel(driver.driverId)}</h4>
                      <div className="mt-2 text-sm">
                        <p><span className="font-medium">Task:</span> {driver.task}</p>
                        {driver.notes && <p><span className="font-medium">Notes:</span> {driver.notes}</p>}
                        <p><span className="font-medium">Start:</span> {new Date(driver.startDate).toLocaleString()}</p>
                        <p><span className="font-medium">End:</span> {new Date(driver.endDate).toLocaleString()}</p>
                        {driver.isImmediate && <p className="text-green-600 font-medium">Immediate</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full">
                {/* <SecureImage
                  fileKey={driverImg}
                  alt="Assign Driver"
                  width="296px"
                /> */}
                <p className="text-sm text-gray-500 text-center mt-4">
                  Please assign driver for the task
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Modal Footer */}
        <div className="flex gap-4 justify-end p-4 border-t">
          <button
            type="button"
            onClick={onSubmit}
            className="w-36 h-10 rounded-lg bg-[#444FBD] text-white transition duration-200 hover:bg-opacity-90"
            disabled={assignedDrivers.length === 0}
          >
            Save
          </button>
          <button
            type="button"
            onClick={handleCancel}
            className="w-36 h-10 rounded-lg bg-white text-gray-800 border border-gray-300 hover:border-gray-400 transition duration-200"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssignDriverModal;